<template>
  <div class="flex flex-col gap-y-3" v-if="schema && schema.length > 0">
    <div v-for="field in schema" :key="field.key" class="flex flex-col gap-y-2">
      <label :for="field.key" class="text-sm md:text-md">
        {{ field.label }}
        <span v-if="field.required" class="text-red-500">*</span>
      </label>

      <!-- Text input -->
      <input
        v-if="field.type === 'text'"
        :id="field.key"
        type="text"
        class="p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        :disabled="disabled"
        v-model="values[field.key]"
        @input="emitChange"
      />

      <!-- Time input -->
      <input
        v-else-if="field.type === 'time'"
        :id="field.key"
        type="time"
        class="p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
        :required="field.required"
        :disabled="disabled"
        v-model="values[field.key]"
        @input="emitChange"
      />

      <!-- Number input -->
      <input
        v-else-if="field.type === 'number'"
        :id="field.key"
        type="number"
        class="p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
        :placeholder="field.placeholder || ''"
        :min="field.min"
        :max="field.max"
        :step="field.step || 1"
        :required="field.required"
        :disabled="disabled"
        v-model="values[field.key]"
        @input="emitChange"
      />

      <!-- Select -->
      <select
        v-else-if="field.type === 'select'"
        :id="field.key"
        class="p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
        :required="field.required"
        :disabled="disabled"
        v-model="values[field.key]"
        @change="emitChange"
      >
        <option value="">{{ field.placeholder || "-- Seleccione --" }}</option>
        <option
          v-for="opt in field.options"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>

      <!-- Textarea -->
      <textarea
        v-else-if="field.type === 'textarea'"
        :id="field.key"
        rows="3"
        class="p-2 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        :disabled="disabled"
        v-model="values[field.key]"
        @input="emitChange"
      ></textarea>

      <!-- Date input -->
      <input
        v-else-if="field.type === 'date'"
        :id="field.key"
        type="date"
        class="p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
        :required="field.required"
        :disabled="disabled"
        v-model="values[field.key]"
        @input="emitChange"
      />

      <!-- Email input -->
      <input
        v-else-if="field.type === 'email'"
        :id="field.key"
        type="email"
        class="p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        :disabled="disabled"
        v-model="values[field.key]"
        @input="emitChange"
      />

      <!-- Phone input -->
      <PhoneInput
        v-else-if="field.type === 'phone' || field.type === 'tel'"
        v-model="values[field.key]"
        :id="field.key"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        :disabled="disabled"
        @update:model-value="emitChange"
      />

      <!-- Checkbox -->
      <div
        v-else-if="field.type === 'checkbox'"
        class="flex items-center gap-x-2"
      >
        <input
          :id="field.key"
          type="checkbox"
          class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          :required="field.required"
          :disabled="disabled"
          v-model="values[field.key]"
          @change="emitChange"
        />
        <span class="text-sm text-gray-600">{{ field.description || "" }}</span>
      </div>

      <!-- Default to text input -->
      <input
        v-else
        :id="field.key"
        type="text"
        class="p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        :disabled="disabled"
        v-model="values[field.key]"
        @input="emitChange"
      />

      <!-- Helper text -->
      <p
        v-if="field.description && field.type !== 'checkbox'"
        class="text-xs text-gray-500"
      >
        {{ field.description }}
      </p>

      <!-- Error message -->
      <p v-if="errors[field.key]" class="text-xs text-red-400">
        {{ errors[field.key] }}
      </p>
    </div>
  </div>
</template>

<script>
import PhoneInput from "@/components/ui/PhoneInput.vue";
import { isPossiblePhoneNumberValue } from "@/shared/utils/phone-number";

export default {
  name: "DynamicCustomFields",
  components: {
    PhoneInput,
  },
  props: {
    /**
     * Schema array defining the custom fields
     * Each field object should have:
     * - key: string (field identifier)
     * - label: string (display label)
     * - type: 'text' | 'time' | 'number' | 'select' | 'textarea' | 'date' | 'email' | 'phone' | 'checkbox'
     * - required: boolean
     * - placeholder?: string
     * - description?: string
     * - options?: Array<{ value: string, label: string }> (for select type)
     * - min?: number (for number type)
     * - max?: number (for number type)
     * - step?: number (for number type)
     */
    schema: {
      type: Array,
      default: () => [],
    },
    /**
     * Object with field values (v-model)
     */
    modelValue: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Disable all fields
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Object with field validation errors
     * Keys are field keys, values are error messages
     */
    errors: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      values: {},
    };
  },
  watch: {
    modelValue: {
      handler(newVal) {
        this.values = { ...newVal };
      },
      immediate: true,
      deep: true,
    },
    schema: {
      handler(newSchema) {
        // Initialize default values for any missing fields
        if (newSchema && newSchema.length > 0) {
          const newValues = { ...this.values };
          newSchema.forEach((field) => {
            if (!(field.key in newValues)) {
              // Set default values based on field type
              if (field.type === "checkbox") {
                newValues[field.key] = false;
              } else if (field.type === "number") {
                newValues[field.key] = field.default ?? null;
              } else {
                newValues[field.key] = field.default ?? "";
              }
            }
          });
          this.values = newValues;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    emitChange() {
      this.$emit("update:modelValue", { ...this.values });
    },
    /**
     * Validate all required fields
     * @returns {{ valid: boolean, errors: Object }}
     */
    validate() {
      const errors = {};

      if (!this.schema || this.schema.length === 0) {
        return { valid: true, errors: {} };
      }

      for (const field of this.schema) {
        const value = this.values[field.key];

        // Check required fields
        if (field.required) {
          if (value === undefined || value === null || value === "") {
            errors[field.key] = `${field.label} es requerido`;
            continue;
          }
        }

        // Type-specific validation
        if (value !== undefined && value !== null && value !== "") {
          if (field.type === "email" && !this.isValidEmail(value)) {
            errors[field.key] = `${field.label} debe ser un correo válido`;
          }

          if (
            (field.type === "phone" || field.type === "tel") &&
            !this.isValidPhone(value)
          ) {
            errors[
              field.key
            ] = `${field.label} debe ser un teléfono internacional válido`;
          }

          if (field.type === "number") {
            const numValue = Number(value);
            if (field.min !== undefined && numValue < field.min) {
              errors[
                field.key
              ] = `${field.label} debe ser mayor o igual a ${field.min}`;
            }
            if (field.max !== undefined && numValue > field.max) {
              errors[
                field.key
              ] = `${field.label} debe ser menor o igual a ${field.max}`;
            }
          }
        }
      }

      return {
        valid: Object.keys(errors).length === 0,
        errors,
      };
    },
    /**
     * Get all field values
     * @returns {Object}
     */
    getValues() {
      return { ...this.values };
    },
    /**
     * Reset all field values
     */
    reset() {
      this.values = {};
      this.$emit("update:modelValue", {});
    },
    /**
     * Simple email validation
     * @param {string} email
     * @returns {boolean}
     */
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    isValidPhone(phone) {
      return isPossiblePhoneNumberValue(phone);
    },
  },
};
</script>

<style lang="scss" scoped></style>
